type Consent = 'accepted' | 'rejected' | null;

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

const CONSENT_KEY = 'lp_cookie_consent';

function getConsent(): Consent {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    if (v === 'accepted' || v === 'rejected') return v;
    return null;
  } catch {
    return null;
  }
}

function hasConsent(): boolean {
  return getConsent() === 'accepted';
}

let initialized = false;

function injectScript(src: string) {
  if (typeof document === 'undefined') return;
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

/**
 * Google Consent Mode に従い、タグは常に読み込むが同意前は analytics_storage: 'denied' で送信を抑止する。
 * これにより「タグが検出されない」問題を解消しつつ、同意前は計測データを送らない。
 * ※同意が denied の間は GA 管理画面のレポートにデータが表示されず「データを受信していません」となる。
 *   「ウェブサイトをテストする」はタグの存在のみ確認するため OK になることがある。
 */
function initGA4(measurementId: string) {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: any[]) {
      window.dataLayer!.push(args);
    };

  // index.html で既に consent default を設定済み。同一状態で上書きし、gtag が確実に認識するようにする。
  const consentState = hasConsent() ? 'granted' : 'denied';
  window.gtag('consent', 'default', {
    analytics_storage: consentState,
    ad_storage: consentState,
    ad_user_data: consentState,
    ad_personalization: consentState,
    wait_for_update: 500,
  });

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    anonymize_ip: true,
  });

  injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`);
}

/**
 * 同意状態を GA に反映（同意バナーで「同意」したときに呼ぶ）
 */
export function updateAnalyticsConsent(granted: boolean) {
  if (typeof window.gtag !== 'function') return;
  const state = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
}

/**
 * Analytics初期化
 * - 測定IDが設定されていれば常に gtag を読み込む（検出・検証可能に）
 * - Consent Mode により同意前は analytics_storage: 'denied' で送信しない
 */
export function initAnalytics() {
  if (initialized) return;

  const env = (import.meta as any).env || (window as any).__ENV__ || {};
  const gaMeasurementId = env.VITE_GA_MEASUREMENT_ID as string | undefined;

  if (gaMeasurementId) {
    initGA4(gaMeasurementId);
  } else if (typeof window !== 'undefined' && (env as { MODE?: string }).MODE !== 'test') {
    console.warn(
      '[Analytics] VITE_GA_MEASUREMENT_ID が未設定です。.env に GA4 測定ID（G-XXXXXXXXXX）を設定してください。'
    );
  }

  initialized = true;
}

/**
 * ページビュー（同意済み＆初期化済みの場合のみ送信）
 */
export function trackPageView(path: string) {
  if (!hasConsent()) return;
  if (!initialized) return;
  if (typeof window.gtag !== 'function') return;

  const env = (import.meta as any).env || (window as any).__ENV__ || {};
  const gaMeasurementId = env.VITE_GA_MEASUREMENT_ID as string | undefined;
  if (!gaMeasurementId) return;

  window.gtag('event', 'page_view', {
    page_path: path,
  });
}

/**
 * カスタムイベント送信（同意済み＆初期化済みの場合のみ送信）
 */
export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (!hasConsent()) return;
  if (!initialized) return;
  if (typeof window.gtag !== 'function') return;
  if (!eventName) return;

  window.gtag('event', eventName, params || {});
}


